/**
 * Экран AI-торговли: выбор пары и ставки → «скан» → серия Smart Scaling → результат.
 * Визуал по design-spec §7 (AI): Signal Setup, analysis-card, direction-card.
 *
 * Серию считает сервер. Важное отличие от прошлой версии: ответ приходит не
 * целиком, а по частям — `POST /api/trade/cycle` возвращает `runId`, а шаги
 * подтягиваются опросом. Иначе в режиме реальных демо-сделок запрос висел бы
 * всю экспирацию (и обрывался бы по таймауту), а на платформе сделки не
 * появлялись бы вовсе.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { ApiError, api, apiMessage } from '../lib/api.js';
import type { Asset, SettingsPayload, TradeRun } from '../lib/api.js';
import { haptic } from '../lib/telegram.js';
import { money } from '../lib/format.js';
import { useApp } from '../state/app.js';

type Phase = 'setup' | 'analyzing' | 'trading' | 'result';

/** Как часто спрашиваем сервер о состоянии серии. */
const POLL_MS = 1000;

export function TradeScreen() {
  const { me, patchMe, showToast, setTab, t } = useApp();
  const [assets, setAssets] = useState<Asset[]>([]);
  const [settings, setSettings] = useState<SettingsPayload | null>(null);
  const [pair, setPair] = useState('');
  const [amount, setAmount] = useState(5);
  const [phase, setPhase] = useState<Phase>('setup');
  const [run, setRun] = useState<TradeRun | null>(null);
  const [starting, setStarting] = useState(false);

  /** Идентификатор серии, за которой следим; null — следить не за чем. */
  const watching = useRef<string | null>(null);

  useEffect(() => {
    void api
      .assets()
      .then(({ assets: list }) => {
        setAssets(list);
        if (list[0]) setPair((current) => current || list[0]!.symbol);
      })
      .catch(() => undefined);
    void api.settings().then(setSettings).catch(() => undefined);
  }, []);

  /** Применяем состояние серии к экрану: шаги, фаза, баланс и токены. */
  const apply = useCallback(
    (next: TradeRun) => {
      // ответ незнакомой формы — почти наверняка старая страница из кэша
      // Telegram против обновлённого API. Рисовать по нему нельзя: получится
      // пустая серия с нулевым результатом (так и вышло 2026-08-20)
      if (!next || typeof next.status !== 'string' || !Array.isArray(next.steps)) {
        setPhase('setup');
        showToast(t('app.trade.outdated'));
        return;
      }

      setRun(next);

      if (next.status === 'running') {
        // «анализ» держим только до первой сделки — дальше показываем шаги
        setPhase(next.steps.length > 0 || next.pending ? 'trading' : 'analyzing');
        return;
      }

      watching.current = null;
      setPhase(next.status === 'failed' && next.steps.length === 0 ? 'setup' : 'result');

      if (next.status === 'failed' && next.steps.length === 0) {
        showToast(t(next.errorKey ?? 'app.trade.failed'));
      }

      if (me) {
        patchMe({
          user: { ...me.user, tokens: next.tokensLeft },
          balances: { ...me.balances, demo: next.balance },
        });
      }
    },
    [me, patchMe, showToast, t],
  );

  /** Опрос состояния серии — пока она идёт. */
  useEffect(() => {
    if (!run || run.status !== 'running') return;
    watching.current = run.runId;

    let cancelled = false;
    const timer = globalThis.setTimeout(() => {
      void api
        .tradeRun(run.runId)
        .then((next) => {
          if (!cancelled && watching.current === next.runId) apply(next);
        })
        .catch(() => {
          // разрыв связи не должен ронять экран: следующий тик попробует снова
          if (!cancelled) setRun((current) => (current ? { ...current } : current));
        });
    }, POLL_MS);

    return () => {
      cancelled = true;
      globalThis.clearTimeout(timer);
    };
  }, [run, apply]);

  /** Приложение открыли на середине серии — подхватываем её, а не теряем. */
  useEffect(() => {
    void api
      .activeRun()
      .then(({ run: active }) => {
        if (active) apply(active);
      })
      .catch(() => undefined);
    // осознанно один раз при монтировании: дальше состояние ведёт опрос
  }, []);

  async function launch() {
    if (starting) return;
    haptic('medium');
    setStarting(true);
    setPhase('analyzing');
    setRun(null);

    try {
      const started = await api.runCycle({ amount, ...(pair ? { pair } : {}) });
      apply(started);
    } catch (err) {
      setPhase('setup');
      if (err instanceof ApiError && err.isOutOfTokens) {
        showToast(t('app.trade.no_tokens'));
        setTab('tokens');
        return;
      }
      showToast(apiMessage(err, t, 'app.trade.failed'));
    } finally {
      setStarting(false);
    }
  }

  const bets = settings?.options.bets ?? [
    { amount: 1 },
    { amount: 3 },
    { amount: 5 },
    { amount: 10, risk: true },
    { amount: 20, risk: true },
  ];
  const strategy = settings?.current;
  const pairs = assets.length > 0 ? assets : [{ symbol: 'EURUSD_otc', label: 'EUR/USD OTC' } as Asset];

  return (
    <div className="screen">
      {phase === 'setup' && (
        <>
          <section className="card card-lg">
            <div className="ai-head">
              <img className="ai-emblem" src="/icons/trade.svg" alt="" width={64} height={64} />
              <div>
                <span className="kicker">Quantum AI</span>
                <h2 className="ai-title">{t('app.trade.setup_title')}</h2>
              </div>
            </div>

            <div className="stack">
              <div>
                <div className="label">{t('app.trade.pair')}</div>
                <div className="chip-row">
                  {pairs.map((asset) => (
                    <button
                      key={asset.symbol}
                      type="button"
                      className={`chip ${pair === asset.symbol ? 'is-active' : ''}`}
                      onClick={() => {
                        haptic();
                        setPair(asset.symbol);
                      }}
                    >
                      {asset.label ?? asset.symbol}
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <div className="label">{t('app.trade.amount')}</div>
                <div className="chip-row">
                  {bets.map((bet) => (
                    <button
                      key={bet.amount}
                      type="button"
                      className={`chip ${amount === bet.amount ? 'is-active' : ''}`}
                      onClick={() => {
                        haptic();
                        setAmount(bet.amount);
                      }}
                    >
                      ${bet.amount}
                      {bet.risk ? ' RISK' : ''}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {strategy && (
              <div className="ai-strategy-note">
                {t('app.trade.strategy_line', {
                  strategy: strategy.strategy,
                  progression: strategy.progression,
                  maxSteps: strategy.maxSteps,
                  expiration: strategy.expirationSeconds,
                })}
              </div>
            )}

            <button
              type="button"
              className="cta cta-launch cta-block"
              disabled={starting}
              onClick={() => void launch()}
            >
              {t('app.trade.generate')}
            </button>
          </section>

          <section className="card">
            <div className="row-between">
              <span className="label">{t('app.trade.tokens_available')}</span>
              <span className="pill">{me?.user.tokens ?? 0} 💎</span>
            </div>
            <p className="body">{t('app.trade.cycle_note')}</p>
          </section>
        </>
      )}

      {phase === 'analyzing' && (
        <section className="card card-lg ai-analysis">
          <h2 className="ai-title ai-analysis-title">{t('app.trade.analyzing')}</h2>
          <div className="ai-analysis-rows">
            {[t('app.trade.scan_quotes'), 'RSI · MACD', 'MA50 · MA200', t('app.trade.scan_pattern')].map(
              (row) => (
                <div key={row} className="ai-analysis-row">
                  <span>{row}</span>
                  <span className="ai-shimmer" />
                </div>
              ),
            )}
          </div>
        </section>
      )}

      {(phase === 'trading' || phase === 'result') && run && (
        <>
          <section className={`card card-lg direction-card ${run.profit >= 0 ? 'is-up' : 'is-down'}`}>
            <span className="kicker">{run.pair}</span>
            <div className="value-xl">{run.action === 'call' ? 'LONG' : 'SHORT'}</div>
            <span className="pill pill-green">{t('app.trade.smart_scaling')}</span>
            {/* видно, настоящие это сделки или превью — иначе не отличить;
                реальный счёт помечается отдельно: там настоящие деньги */}
            <span className={`pill ${run.market === 'real' ? 'pill-green' : ''}`}>
              {t(
                run.market === 'real'
                  ? 'app.trade.real_badge'
                  : run.live
                    ? 'app.trade.live_badge'
                    : 'app.trade.demo_badge',
              )}
            </span>
          </section>

          <section className="card">
            <div className="steps-list">
              {run.steps.map((step) => (
                <div key={step.index} className={`step-row ${step.won ? 'is-win' : 'is-loss'}`}>
                  <span className="step-index">{t('app.trade.step', { index: step.index })}</span>
                  <span className="step-amount">${money(step.amount)}</span>
                  <span className="step-profit">
                    {step.profit > 0 ? '+' : '−'}${money(Math.abs(step.profit))}
                  </span>
                </div>
              ))}

              {/* открытая сделка: у брокера она живёт всю экспирацию, и без
                  этой строки экран выглядел бы зависшим */}
              {run.pending && (
                <div className="step-row is-pending">
                  <span className="step-index">
                    {t('app.trade.step', { index: run.pending.index })}
                  </span>
                  <span className="step-amount">${money(run.pending.amount)}</span>
                  <span className="step-profit">
                    <span className="ai-shimmer" />
                  </span>
                </div>
              )}
            </div>

            {run.status === 'running' && (
              <p className="step-hint">{t('app.trade.waiting')}</p>
            )}
          </section>
        </>
      )}

      {phase === 'result' && run && (
        <section className="card card-lg victory-card">
          <h2 className="card-title">
            {run.status === 'failed'
              ? t(run.errorKey ?? 'app.trade.failed')
              : t(run.won ? 'app.trade.win_title' : 'app.trade.loss_title')}
          </h2>
          <div className="victory-grid">
            <div>
              <div className="label">{t('app.trade.result')}</div>
              <div className={`victory-value ${run.profit >= 0 ? 'is-win' : 'is-loss'}`}>
                {run.profit >= 0 ? '+' : '−'}${money(Math.abs(run.profit))}
              </div>
            </div>
            <div>
              <div className="label">{t('app.trade.balance')}</div>
              <div className="victory-value">${money(run.balance)}</div>
            </div>
            <div>
              <div className="label">{t('app.trade.tokens_spent')}</div>
              <div className="victory-value">{run.tokensSpent} 💎</div>
            </div>
          </div>

          <div className="row">
            <button
              type="button"
              className="cta cta-launch grow"
              onClick={() => {
                setPhase('setup');
                setRun(null);
              }}
            >
              {t('app.trade.again')}
            </button>
            <button type="button" className="cta cta-secondary" onClick={() => setTab('home')}>
              {t('app.trade.to_menu')}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
